import React, { useState } from 'react';
import { 
  MoreVertical, 
  Eye, 
  Edit, 
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  Star,
  MapPin,
  Package,
  ShoppingBag,
  MessageSquare,
  Shield,
  AlertTriangle,
  Copy,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  RefreshCw,
  Users
} from 'lucide-react';
import useAdminStore from '../../../store/adminStore';
import { toast } from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';

const BusinessTable = ({ 
  businesses, 
  pagination,
  onView, 
  onEdit, 
  onVerify, 
  onRefresh,
  onPageChange,
  isLoading = false 
}) => {
  const [selectedBusinessId, setSelectedBusinessId] = useState(null);
  const [showActions, setShowActions] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(pagination?.limit || 20);
  
  const { deleteBusinessAdmin } = useAdminStore();

  const handleDelete = async () => {
    try {
      await deleteBusinessAdmin(selectedBusinessId);
      setShowDeleteModal(false);
      setDeleteReason('');
      setSelectedBusinessId(null);
      setShowActions(false);
      toast.success('Business deleted successfully');
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Delete business error:', error);
    }
  };

  const handleCopyId = (id) => {
    navigator.clipboard.writeText(id);
    toast.success('Business ID copied!');
  };

  const handlePageChange = (newPage) => {
    if (onPageChange && newPage >= 1 && newPage <= (pagination?.totalPages || 1)) {
      onPageChange(newPage);
    }
  };

  const handleItemsPerPageChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setItemsPerPage(newLimit);
    if (onPageChange) {
      onPageChange(1, newLimit);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      ACTIVE: { color: 'bg-green-50 text-green-700 border-green-200', icon: CheckCircle },
      PENDING: { color: 'bg-yellow-50 text-yellow-700 border-yellow-200', icon: Clock },
      DRAFT: { color: 'bg-gray-50 text-gray-700 border-gray-200', icon: Clock },
      SUSPENDED: { color: 'bg-red-50 text-red-700 border-red-200', icon: XCircle },
      DELETED: { color: 'bg-red-50 text-red-700 border-red-200', icon: AlertTriangle },
      FEATURED: { color: 'bg-purple-50 text-purple-700 border-purple-200', icon: Star },
    };
    
    const config = statusConfig[status] || statusConfig.DRAFT;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${config.color} shadow-neomorph-inset`}>
        <Icon className="h-2.5 w-2.5 mr-0.5" />
        {status}
      </span>
    );
  };

  const getTierBadge = (tier) => {
    const tierConfig = {
      BASIC: 'bg-blue-50 text-blue-700 border-blue-200',
      FEATURED: 'bg-purple-50 text-purple-700 border-purple-200',
      PREMIUM: 'bg-yellow-50 text-yellow-700 border-yellow-200',
      ENTERPRISE: 'bg-green-50 text-green-700 border-green-200',
    };
    
    return (
      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${tierConfig[tier] || tierConfig.BASIC} shadow-neomorph-inset`}>
        {tier}
      </span>
    );
  };

  const formatDate = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch (error) {
      return 'N/A';
    }
  };

  const total = pagination?.total || 0;
  const currentPage = pagination?.page || 1;
  const totalPages = pagination?.totalPages || 1;
  const limit = pagination?.limit || 20;
  const startIndex = (currentPage - 1) * limit + 1;
  const endIndex = Math.min(currentPage * limit, total);

  return (
    <div className="bg-white rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-xs">
          <thead className="bg-[#E8ECF5]">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase tracking-wider">ID</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase tracking-wider">Business</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase tracking-wider">Owner</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase tracking-wider">Category</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase tracking-wider">Status</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase tracking-wider">Tier</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase tracking-wider">Stats</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase tracking-wider">Created</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan="9" className="px-3 py-8 text-center">
                  <div className="flex items-center justify-center space-x-2">
                    <RefreshCw className="h-4 w-4 text-primary-500 animate-spin" />
                    <span className="text-xs text-gray-500">Loading...</span>
                  </div>
                </td>
              </tr>
            ) : businesses.length === 0 ? (
              <tr>
                <td colSpan="9" className="px-3 py-8 text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-50 shadow-neomorph-inset mb-3">
                    <Users className="h-6 w-6 text-gray-400" />
                  </div>
                  <p className="text-xs text-gray-500 font-medium">No businesses found</p>
                </td>
              </tr>
            ) : (
              businesses.map((business) => (
                <tr key={business.id} className="hover:bg-[#E8ECF5] transition-colors">
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="text-[10px] font-mono text-gray-500 bg-[#E8ECF5] px-1.5 py-0.5 rounded shadow-neomorph-inset truncate max-w-[80px]">
                        {business.id}
                      </span>
                      <button
                        onClick={() => handleCopyId(business.id)}
                        className="ml-1 p-0.5 hover:bg-white rounded transition-colors"
                        title="Copy ID"
                      >
                        <Copy className="h-3 w-3 text-gray-400 hover:text-primary-600" />
                      </button>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center">
                      <div className="h-8 w-8 flex-shrink-0">
                        {business.logoUrl ? (
                          <img 
                            src={business.logoUrl} 
                            alt={business.name}
                            className="h-8 w-8 rounded-lg object-cover shadow-neomorph-inset"
                          />
                        ) : (
                          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 shadow-md flex items-center justify-center">
                            <span className="text-white font-semibold text-[10px]">
                              {business.name?.[0]}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="ml-2">
                        <div className="flex items-center">
                          <div className="text-xs font-semibold text-gray-800">
                            {business.name}
                          </div>
                          {business.verified && (
                            <div className="ml-1 p-0.5 bg-green-100 rounded shadow-neomorph-inset" title="Verified">
                              <Shield className="h-2.5 w-2.5 text-green-600" />
                            </div>
                          )}
                        </div>
                        <div className="text-[10px] text-gray-500 flex items-center">
                          <MapPin className="h-2.5 w-2.5 mr-0.5" />
                          {business.city}, {business.state}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-xs font-medium text-gray-800">
                      {business.user?.firstName} {business.user?.lastName}
                    </div>
                    <div className="text-[10px] text-gray-500">
                      {business.user?.email}
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className="text-xs text-gray-700 bg-[#E8ECF5] px-1.5 py-0.5 rounded shadow-neomorph-inset">
                      {business.category}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {getStatusBadge(business.status)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {getTierBadge(business.tier)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="flex items-center space-x-1.5">
                      <div className="flex items-center bg-[#E8ECF5] px-1.5 py-0.5 rounded shadow-neomorph-inset" title="Products">
                        <Package className="h-2.5 w-2.5 text-primary-600 mr-0.5" />
                        <span className="text-[10px] font-medium">{business._count?.products || 0}</span>
                      </div>
                      <div className="flex items-center bg-[#E8ECF5] px-1.5 py-0.5 rounded shadow-neomorph-inset" title="Services">
                        <ShoppingBag className="h-2.5 w-2.5 text-purple-600 mr-0.5" />
                        <span className="text-[10px] font-medium">{business._count?.services || 0}</span>
                      </div>
                      <div className="flex items-center bg-[#E8ECF5] px-1.5 py-0.5 rounded shadow-neomorph-inset" title="Reviews">
                        <MessageSquare className="h-2.5 w-2.5 text-blue-600 mr-0.5" />
                        <span className="text-[10px] font-medium">{business._count?.reviews || 0}</span>
                      </div>
                      <div className="flex items-center bg-[#E8ECF5] px-1.5 py-0.5 rounded shadow-neomorph-inset" title="Rating">
                        <Star className="h-2.5 w-2.5 text-yellow-500 mr-0.5 fill-current" />
                        <span className="text-[10px] font-medium">{business.rating || '0'}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-[10px] text-gray-500">
                    <span className="bg-[#E8ECF5] px-1.5 py-0.5 rounded shadow-neomorph-inset">
                      {formatDate(business.createdAt)}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap relative">
                    <button
                      onClick={() => {
                        setSelectedBusinessId(business.id);
                        setShowActions(!showActions);
                      }}
                      className="p-1 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all text-gray-600"
                    >
                      <MoreVertical className="h-3.5 w-3.5" />
                    </button>
                    
                    {selectedBusinessId === business.id && showActions && (
                      <div className="absolute right-0 mt-1 w-48 bg-white rounded-xl shadow-neomorph border border-gray-100 z-10 overflow-hidden">
                        <button
                          onClick={() => {
                            onView(business);
                            setShowActions(false);
                            setSelectedBusinessId(null);
                          }}
                          className="flex items-center w-full px-3 py-2 text-xs text-gray-700 hover:bg-[#E8ECF5] transition-colors"
                        >
                          <Eye className="h-3.5 w-3.5 mr-2 text-primary-600" />
                          View
                        </button>
                        
                        <button
                          onClick={() => {
                            onEdit(business);
                            setShowActions(false);
                            setSelectedBusinessId(null);
                          }}
                          className="flex items-center w-full px-3 py-2 text-xs text-gray-700 hover:bg-[#E8ECF5] transition-colors"
                        >
                          <Edit className="h-3.5 w-3.5 mr-2 text-blue-600" />
                          Edit
                        </button>
                        
                        <button
                          onClick={() => {
                            onVerify(business, !business.verified);
                            setShowActions(false);
                            setSelectedBusinessId(null);
                          }}
                          className="flex items-center w-full px-3 py-2 text-xs text-gray-700 hover:bg-[#E8ECF5] transition-colors"
                        >
                          <Shield className={`h-3.5 w-3.5 mr-2 ${business.verified ? 'text-green-600' : 'text-gray-400'}`} />
                          {business.verified ? 'Unverify' : 'Verify'}
                        </button>
                        
                        <button
                          onClick={() => {
                            handleCopyId(business.id);
                            setShowActions(false);
                            setSelectedBusinessId(null);
                          }}
                          className="flex items-center w-full px-3 py-2 text-xs text-gray-700 hover:bg-[#E8ECF5] transition-colors"
                        >
                          <Copy className="h-3.5 w-3.5 mr-2 text-primary-600" />
                          Copy ID
                        </button>
                        
                        <div className="border-t border-gray-100 my-1"></div>
                        
                        <button
                          onClick={() => {
                            setSelectedBusinessId(business.id);
                            setShowDeleteModal(true);
                            setShowActions(false);
                          }}
                          className="flex items-center w-full px-3 py-2 text-xs text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-2" />
                          Delete
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {!isLoading && businesses.length > 0 && (
        <div className="px-3 py-2 border-t border-gray-200 bg-gray-50/50">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
            <div className="flex items-center space-x-1.5">
              <label className="text-[10px] text-gray-600">Show:</label>
              <select
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                className="px-1.5 py-0.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-[10px]"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-[10px] text-gray-500">per page</span>
            </div>

            <div className="text-[10px] text-gray-600">
              Showing <span className="font-medium text-gray-800">{startIndex}</span> to{' '}
              <span className="font-medium text-gray-800">{endIndex}</span> of{' '}
              <span className="font-medium text-gray-800">{total}</span>
            </div>

            <div className="flex items-center space-x-0.5">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1 || isLoading}
                className={`p-1 rounded-lg transition-all ${
                  currentPage === 1 || isLoading
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                }`}
                title="First"
              >
                <ChevronsLeft className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || isLoading}
                className={`p-1 rounded-lg transition-all ${
                  currentPage === 1 || isLoading
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                }`}
                title="Previous"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>

              <div className="flex items-center space-x-0.5">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`w-6 h-6 rounded-lg text-[10px] font-medium transition-all ${
                        currentPage === pageNum
                          ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md shadow-primary-500/30'
                          : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || isLoading}
                className={`p-1 rounded-lg transition-all ${
                  currentPage === totalPages || isLoading
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                }`}
                title="Next"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages || isLoading}
                className={`p-1 rounded-lg transition-all ${
                  currentPage === totalPages || isLoading
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                }`}
                title="Last"
              >
                <ChevronsRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-3 pt-3 pb-16 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowDeleteModal(false)}></div>

            <div className="inline-block align-bottom bg-white rounded-xl px-4 pt-4 pb-4 text-left overflow-hidden shadow-neomorph transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-5 border border-primary-100/30">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-xl bg-gradient-to-br from-red-500 to-red-600 shadow-lg shadow-red-500/30 sm:mx-0">
                  <AlertTriangle className="h-6 w-6 text-white" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                  <div className="flex items-center mb-1">
                    <div className="w-1 h-4 bg-gradient-to-b from-red-400 to-red-600 rounded-full mr-2" />
                    <h3 className="text-base font-semibold text-gray-800">Delete Business</h3>
                  </div>
                  <p className="text-xs text-gray-500">This action cannot be undone.</p>
                </div>
              </div>

              <div className="mt-3">
                <textarea
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  placeholder="Reason (optional)..."
                  className="block w-full px-2.5 py-1.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500/50 min-h-[60px] resize-none"
                  maxLength={500}
                />
              </div>

              <div className="mt-4 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={isLoading}
                  className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-md px-3 py-1.5 bg-gradient-to-r from-red-600 to-red-700 text-xs font-medium text-white hover:shadow-lg hover:shadow-red-500/40 focus:outline-none sm:ml-3 sm:w-auto disabled:opacity-50 transition-all"
                >
                  {isLoading ? 'Deleting...' : 'Delete'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteReason('');
                  }}
                  className="mt-2 w-full inline-flex justify-center rounded-lg border border-gray-100 shadow-neomorph px-3 py-1.5 bg-white text-xs font-medium text-gray-700 hover:shadow-neomorph-hover focus:outline-none sm:mt-0 sm:w-auto transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
      `}</style>
    </div>
  );
};

export default BusinessTable;